import { usePersistedDataStore } from '../../store.js';
import { fetchPresignedUrlsFromBackend } from '../Workups/WorkupDownload.js';
import { regenerateWorkups } from '../../../services/regenerateWorkupsService.js';
import { getWorkupS3FileName } from '../../../common/mappings.js';
import { isCaseSettled } from '../SummaryCaseTable/utils/helpers.js';
import { uploadFileToS3Presigned } from '../Offers/s3UploadService.js';
import { getMostRecentOfferValue } from '../../../utils/mostRecentOfferUtils.js';
import { getConversionFactor } from '../../../utils/conversionUtils.js';
import { newLoadClientIds } from '../../../services/initializeFetchUtils.js';
import logger from '../../../utils/logger';

// Eventually, we should expand this to handle settlements etc.

// for now, setting up a workup sending flow.
  // requires replicating the validation flow + dialog display.
// then will rebuild offer sending flow, and the combined offers + workups flow.

// the EMAIL_TYPES from emailService.js should be deleted once everything is migrated to this factory.
export const EMAIL_TYPES = {
  WORKUPS: 'workups',
  OFFERS: 'offers',
  OFFERS_AND_WORKUPS: 'offers_and_workups'
};

/**
 * Fetch PDF bytes from URL
 */
const fetchPdfBytes = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  return new Uint8Array(await response.arrayBuffer());
};

/**
 * Merge PDFs and upload to S3
 * @param {Array<string>} urls - Array of presigned URLs to PDF files
 * @returns {Promise<Object>} Upload result with downloadUrl and filename
 */
const mergeAndUploadToS3 = async (urls, nameSuffix = '') => {
  try {
    logger.log('🔄 [EMAIL FACTORY] Merging PDFs...');
    
    // Dynamically import pdf-lib
    const { PDFDocument } = await import('pdf-lib');
    
    const mergedPdf = await PDFDocument.create();

    // Merge all PDFs
    for (const url of urls) {
      try {
        const pdfBytes = await fetchPdfBytes(url);
        const pdf = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => mergedPdf.addPage(page));
      } catch (error) {
        logger.error(`Error merging PDF from ${url}:`, error);
      }
    }

    const mergedPdfBytes = await mergedPdf.save();
    logger.log('✅ [EMAIL FACTORY] PDFs merged successfully:', {
      pdfCount: urls.length,
      mergedSize: mergedPdfBytes.length
    });

    // Generate unique S3 filename using timestamp
    const timestamp = Date.now();
    const s3FileName = `Workups/workup_${timestamp}${nameSuffix}.pdf`;
    
    // Generate user-facing download filename with date
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const downloadFileName = `Aventine Workups_${month}_${day}_${year}.pdf`;
    
    logger.log('📁 [EMAIL FACTORY] S3 file details:', {
      s3FileName,
      downloadFileName,
      blobSize: mergedPdfBytes.length
    });

    // Create blob and upload to S3 via presigned URL (direct to S3 to avoid backend filesize limits)
    const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    logger.log('🔄 [EMAIL FACTORY] Uploading merged PDF to S3 (presigned)...');
    const s3Url = await uploadFileToS3Presigned(blob, s3FileName, 'negotiation-files', downloadFileName);
    
    logger.log('✅ [EMAIL FACTORY] S3 upload successful!');
    logger.log('🔗 [EMAIL FACTORY] S3 URL:', s3Url);

    return {
      success: true,
      downloadUrl: s3Url,
      csvFilename: downloadFileName,
      s3FileName: s3FileName,
      blobSize: mergedPdfBytes.length
    };

  } catch (error) {
    logger.error('❌ [EMAIL FACTORY] Failed to merge and upload PDFs to S3:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

const fileNameOf = (file) => file?.Filename || file?.Key?.split('/').pop() || '';

const workupNamesForParcel = (parcelId, s3Files, { evidenceTypesByParcel, defaultFilename, salesFilename, equityCandidates }) => {
  const types = evidenceTypesByParcel?.[parcelId] ?? evidenceTypesByParcel?.[String(parcelId)];
  const selected = Array.isArray(types) && types.length ? types : null;
  if (!selected) return [defaultFilename];
  const names = [];
  if (selected.includes('Unequal')) names.push(salesFilename);
  if (selected.includes('Excessive')) {
    const equityName = equityCandidates.find((name) => !names.includes(name) && (s3Files || []).some((file) => fileNameOf(file) === name));
    if (equityName) names.push(equityName);
    else logger.warn(`No assessment workup for parcel ${parcelId}. Looked for ${equityCandidates.join(', ')}`);
  }
  return names;
};

/** Sales PDF, then that parcel's assessment PDF when Excessive is selected. Shared by the sheet link and the batch file. */
const workupUrlGroups = async (parcelIds, workupType, evidenceTypesByParcel) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const taxYear = negotiationObj?.TaxYear ? parseInt(negotiationObj.TaxYear) : null;
  if (!taxYear || !parcelIds?.length) return [];
  const reportType = negotiationObj.reportType || 'standard';
  const names = {
    evidenceTypesByParcel,
    defaultFilename: `${getWorkupS3FileName(reportType, workupType)}.pdf`,
    salesFilename: `${getWorkupS3FileName(negotiationObj.villageRun === 1 ? 'village' : 'standard', workupType)}.pdf`,
    equityCandidates: [...new Set([
      `${getWorkupS3FileName('assessment', workupType)}.pdf`,
      `${getWorkupS3FileName('assessment', 'onepagermaps')}.pdf`,
    ])],
  };
  return fetchPresignedUrlsFromBackend(
    parcelIds,
    taxYear,
    (parcelId, s3Files) => workupNamesForParcel(parcelId, s3Files, names),
  );
};

/**
 * One spreadsheet link per parcel. Same files as the batch PDF; a parcel with both types is merged on its own.
 * @returns {Promise<Object<string, string>>} parcel id -> url
 */
export const offerSheetWorkupLinks = async (rows, workupType = 'fnmaSummary') => {
  const parcelIds = (rows || []).map((row) => row.parcel_id || row.id).filter(Boolean);
  const evidenceTypesByParcel = {};
  (rows || []).forEach((row) => {
    const id = row.parcel_id || row.id;
    const types = Array.isArray(row.SelectedEvidenceTypes)
      ? row.SelectedEvidenceTypes.filter((type) => type === 'Unequal' || type === 'Excessive')
      : [];
    if (id && types.length) evidenceTypesByParcel[String(id)] = types;
  });
  const urlGroups = await workupUrlGroups(parcelIds, workupType, evidenceTypesByParcel);
  const links = {};
  for (let index = 0; index < parcelIds.length; index += 1) {
    const urls = urlGroups[index] || [];
    const parcelId = String(parcelIds[index]);
    if (urls.length > 1) {
      const merged = await mergeAndUploadToS3(urls, `_${index}`);
      if (merged.success && merged.downloadUrl) links[parcelId] = merged.downloadUrl;
      else if (urls[0]) links[parcelId] = urls[0];
    } else if (urls[0]) {
      links[parcelId] = urls[0];
    }
  }
  return links;
};

/**
 * Prepares workup email data.
 * Batch merge order is parcel order. When a parcel's SelectedEvidenceTypes includes
 * Excessive, that parcel's assessment PDF is appended immediately after its sales PDF.
 * @param {Array} parcelIds - Array of parcel IDs
 * @param {string} workupType - Type of workup ('fnmaSummary' or 'onepagermaps'); determines which S3 file to fetch
 * @param {Object<string, string[]>|null} [evidenceTypesByParcel] - parcel id -> ['Unequal'|'Excessive']
 */
const prepareWorkupsEmail = async (parcelIds, workupType = 'fnmaSummary', evidenceTypesByParcel = null) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  
  if (!negotiationObj) {
    throw new Error('Negotiation object not found');
  }

  const taxYear = negotiationObj.TaxYear ? parseInt(negotiationObj.TaxYear) : null;
  if (!taxYear) {
    throw new Error('TaxYear is required but not found');
  }
  
  const urlGroups = await workupUrlGroups(parcelIds, workupType, evidenceTypesByParcel);
  
  // Prepare simplified data array
  const built = parcelIds.map((parcelId, idx) => {
    const caseData = negotiationObj.cases?.find(c => c.parcel_id === parcelId);
    const urls = urlGroups[idx] || [];
    
    if (!caseData) {
      logger.warn(`Case data not found for parcel ${parcelId}`);
      return null;
    }

    return {
      Address: caseData.Address || '',
      parcel_id: parcelId,
      scar_id: caseData.SCARIndexNumber || '',
      has_workup: urls.length > 0,
      urls,
    };
  }).filter(Boolean);

  const data = built.map(({ urls, ...item }) => item);

  // Filter out parcels without workups
  const parcelsWithWorkups = data.filter(item => item.has_workup);
  const parcelsWithoutWorkups = data.filter(item => !item.has_workup);

  if (parcelsWithWorkups.length === 0) {
    throw new Error('No workup files found. Please regenerate workups first.');
  }

  // Sales PDF, then that parcel's assessment PDF, then the next parcel
  const workupUrls = built.filter((item) => item.has_workup).flatMap((item) => item.urls);

  // Merge PDFs and upload to S3
  let downloadUrl = null;
  let downloadFilename = null;

  if (workupUrls.length > 0) {
    logger.log('🔄 [EMAIL FACTORY] Merging and uploading workup PDFs to S3...');

    const s3UploadResult = await mergeAndUploadToS3(workupUrls);

    if (s3UploadResult.success) {
      downloadUrl = s3UploadResult.downloadUrl;
      downloadFilename = s3UploadResult.csvFilename;
      logger.log('✅ [EMAIL FACTORY] Merged workup PDF uploaded to S3:', {
        downloadUrl,
        downloadFilename
      });
    } else {
      logger.warn('⚠️ [EMAIL FACTORY] Failed to upload to S3, continuing without download URL:', s3UploadResult.error);
    }
  }

  // Get case info from store
  const totalCases = negotiationObj.cases?.length || 0;
  const settledCases = negotiationObj.cases?.filter(c => isCaseSettled(c)).length || 0;
  const openCases = totalCases - settledCases;

  return {
    data: parcelsWithWorkups,
    emailOptions: {
      templateType: EMAIL_TYPES.WORKUPS,
      data: parcelsWithWorkups,
      downloadUrl: downloadUrl,
      csvFilename: downloadFilename,
      caseInfo: {
        openCases,
        settledCases
      }
    },
    missingItems: parcelsWithoutWorkups,
    message: parcelsWithoutWorkups.length > 0 
      ? `${parcelsWithWorkups.length} workup(s) ready to send. ${parcelsWithoutWorkups.length} parcel(s) missing workups.`
      : `${parcelsWithWorkups.length} workup(s) ready to send`
  };
};

/**
 * Prepares offers email data
 */
export const prepareOffersEmail = async (parcelIds) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  
  if (!negotiationObj) {
    throw new Error('Negotiation object not found');
  }

  const data = parcelIds.map(parcelId => {
    const caseData = negotiationObj.cases?.find(c => c.parcel_id === parcelId);
    
    if (!caseData) {
      logger.warn(`Case data not found for parcel ${parcelId}`);
      return null;
    }

    // Get latest Aventine offer (AV) and convert to MV
    const negotiations = caseData.Negotiations || [];
    const latestOfferAV = getMostRecentOfferValue(negotiations, 'Aventine');

    if (latestOfferAV === null || latestOfferAV === undefined || isNaN(Number(latestOfferAV)) || Number(latestOfferAV) <= 0) {
      logger.warn(`No valid Aventine offer found for parcel ${parcelId}`);
      return null;
    }

    const offerAVNum = Number(latestOfferAV);
    const eq = caseData.EQ;
    const rar = caseData.RAR;
    const conversionFactor = getConversionFactor(eq, rar);

    const offerMV = offerAVNum / conversionFactor

    return {
      Address: caseData.Address || '',
      parcel_id: parcelId,
      scar_id: caseData.SCARIndexNumber || caseData.scar_id || '',
      has_offer: true,
      OfferValue: offerMV
    };
  }).filter(Boolean);

  if (data.length === 0) {
    throw new Error('No valid offers found for selected parcels');
  }

  // Get case info from store
  
  // const totalCases = negotiationObj.cases?.length || 0;
  //     // Step 3: Calculate case statistics
  //     const totalCases = allRows.length;
  //     // here, invoke the load client ids
  //     logger.log('negotiation obj', negotiationObj);
      // hardcode showSettledCases true for this load
      negotiationObj.showSettledCases = true;
      const { client_ids: clientIds } = await newLoadClientIds(negotiationObj);
      const totalCases = clientIds.length;
      // const settledCases = allRows.filter(row => isCaseSettled(row)).length;
      const openCases = negotiationObj.cases?.filter(row => !isCaseSettled(row)).length;
      // const openCases = totalCases - settledCases;
      const settledCases = totalCases - openCases;
  
  // const openCases = negotiationObj.cases?.filter(c => !isCaseSettled(c)).length || 0;
  // const openCases = totalCases - settledCases;

  return {
    data,
    emailOptions: {
      templateType: EMAIL_TYPES.OFFERS,
      data: data,
      downloadUrl: null, // TODO: Implement offers document URL if needed
      caseInfo: {
        openCases,
        settledCases
      }
    },
    missingItems: [],
    message: `${data.length} offer(s) ready to send`
  };
};


/**
 * Send workups email
 * @param {Array} parcelIds - Array of parcel IDs
 * @param {string} workupType - Type of workup ('fnmaSummary' or 'onepagermaps')
 */
export const sendWorkups = async (parcelIds, workupType = 'fnmaSummary', userId) => {
  logger.log('🔄 [EMAIL FACTORY] Sending workups for', parcelIds.length, 'parcels with type:', workupType);
  
  if (!parcelIds || parcelIds.length === 0) {
    return {
      success: false,
      error: 'No parcel IDs provided'
    };
  }

  try {
    // First, regenerate workups with specified report type
    logger.log('🔄 [EMAIL FACTORY] Regenerating workups before sending...');

    const regenerateResult = await regenerateWorkups({
      parcelIds,
      reportTypes: [workupType],
      userId,
    });

    if (!regenerateResult.success) {
      return {
        success: false,
        error: regenerateResult.message || 'Failed to regenerate workups'
      };
    }

    logger.log('✅ [EMAIL FACTORY] Workups regenerated successfully');

    // Now prepare the email (pass workupType so correct S3 file is fetched)
    const result = await prepareWorkupsEmail(parcelIds, workupType);


    logger.log('result of workups email factory', result);

    const downloadUrl =
      result?.emailOptions?.downloadUrl ||
      result?.downloadUrl ||
      null;

    if (!downloadUrl) {
      logger.error('❌ [EMAIL FACTORY] Workup email is missing required download URL', {
        parcelIdsCount: parcelIds.length,
        workupType,
      });
      return {
        success: false,
        error: 'Workup email is missing required download link. Please regenerate workups and try again.',
      };
    }

    return {
      success: true,
      ...result
    };
  } catch (error) {
    logger.error('❌ [EMAIL FACTORY] Error sending workups:', error);
    return {
      success: false,
      error: error.message || 'Failed to send workups'
    };
  }
};

/**
 * Send offers email
 */
export const sendOffers = async (parcelIds) => {
  logger.log('🔄 [EMAIL FACTORY] Sending offers for', parcelIds.length, 'parcels');
  
  if (!parcelIds || parcelIds.length === 0) {
    return {
      success: false,
      error: 'No parcel IDs provided'
    };
  }

  try {
    const result = await prepareOffersEmail(parcelIds);
    return {
      success: true,
      ...result
    };
  } catch (error) {
    logger.error('❌ [EMAIL FACTORY] Error sending offers:', error);
    return {
      success: false,
      error: error.message || 'Failed to send offers'
    };
  }
};

/**
 * Generate workups and return download URL
 * Used by combined offers+workups flow
 * @param {Array} parcelIds - Array of parcel IDs
 * @param {string} workupType - Type of workup ('fnmaSummary' or 'onepagermaps')
 */
export const generateWorkupsForCombinedFlow = async (parcelIds, workupType = 'fnmaSummary', userId, evidenceTypesByParcel = null) => {
  logger.log('🔄 [EMAIL FACTORY] Generating workups for combined flow:', {
    parcelCount: parcelIds.length,
    workupType
  });
  
  try {
    // Regenerate workups
    const regenerateResult = await regenerateWorkups({
      parcelIds,
      reportTypes: [workupType],
      userId,
    });

    if (!regenerateResult.success) {
      return {
        success: false,
        error: regenerateResult.message || 'Failed to regenerate workups'
      };
    }

    // Prepare workups and get download URL (pass workupType so correct S3 file is fetched)
    const workupsResult = await prepareWorkupsEmail(parcelIds, workupType, evidenceTypesByParcel);
    
    return {
      success: true,
      downloadUrl: workupsResult.emailOptions.downloadUrl,
      data: workupsResult.data
    };
  } catch (error) {
    logger.error('❌ [EMAIL FACTORY] Error generating workups:', error);
    return {
      success: false,
      error: error.message || 'Failed to generate workups'
    };
  }
};

/**
 * Unified helper to prepare offers and optional workups email payload
 * Used by both SummaryCaseTable and EvidenceComparison flows
 * @param {Object} options
 * @param {Array} options.parcelIds - Parcel IDs to include
 * @param {boolean} [options.includeWorkups=false] - Legacy: attach batch workups when true and type is batch
 * @param {{ includeWorkups?: boolean, type?: 'batch'|'individual' }} [options.workupOptions] - When set, attachment uses batch zip only for `includeWorkups && type === 'batch'`
 * @param {boolean} [options.RegenerateWorkups=true] - Whether to regenerate workups (batch attachment only)
 * @param {string} [options.workupType='fnmaSummary'] - Workup report type
 * @param {Object<string, string[]>|null} [options.evidenceTypesByParcel] - parcel id -> selected evidence types; Excessive appends the assessment PDF after sales in the batch file
 */
export const prepareOffersAndOptionalWorkupsEmail = async (options) => {
  const {
    parcelIds,
    includeWorkups = false,
    RegenerateWorkups = true,
    workupType = 'fnmaSummary',
    workupOptions: workupOptionsInput,
    userId,
    evidenceTypesByParcel = null,
  } = options || {};

  const workupOptions = workupOptionsInput ?? {
    includeWorkups,
    type: 'batch',
  };
  const attachBatchWorkups =
    !!workupOptions.includeWorkups && workupOptions.type === 'batch';

  if (!parcelIds || parcelIds.length === 0) {
    return {
      success: false,
      error: 'No parcel IDs provided',
    };
  }

  try {
    // Always prepare offers email data
    const offersResult = await prepareOffersEmail(parcelIds);

    let workupsResult = null;
    if (attachBatchWorkups) {
      const negotiationObj = usePersistedDataStore.getState().negotiationObj;
      const workupParcelIds = parcelIds.filter((parcelId) => {
        const caseData = negotiationObj?.cases?.find((c) => c.parcel_id === parcelId);
        return !caseData || !isCaseSettled(caseData);
      });

      if (workupParcelIds.length === 0) {
        return {
          success: false,
          error: 'No unsettled cases selected for workups',
        };
      }

      if (RegenerateWorkups) {
        // Full regenerate + fetch
        workupsResult = await generateWorkupsForCombinedFlow(workupParcelIds, workupType, userId, evidenceTypesByParcel);
        if (!workupsResult.success) {
          return {
            success: false,
            error: workupsResult.error || 'Failed to generate workups',
          };
        }
      } else {
        // Reuse existing workups only (no regeneration)
        workupsResult = await prepareWorkupsEmail(workupParcelIds, workupType, evidenceTypesByParcel);
        logger.log('workups result of email factory', workupsResult);
      }
    }

    const workups_download_url = attachBatchWorkups
      ? (workupsResult?.emailOptions?.downloadUrl || workupsResult?.downloadUrl || null)
      : null;

    if (attachBatchWorkups && !workups_download_url) {
      logger.error('❌ [EMAIL FACTORY] Combined offers/workups email is missing required workups download URL', {
        parcelIdsCount: parcelIds.length,
        workupType,
        RegenerateWorkups,
      });
      return {
        success: false,
        error: 'Offers + workups email is missing required workups download link. Please regenerate workups and try again.',
      };
    }

    const emailOptions = {
      templateType: attachBatchWorkups ? EMAIL_TYPES.OFFERS_AND_WORKUPS : EMAIL_TYPES.OFFERS,
      data: offersResult.data,
      offersDownloadUrl: null, // offers XLS handled separately by S3 upload service
      workups_download_url,
      csvFilename: workupsResult?.emailOptions?.csvFilename || null,
      caseInfo: offersResult.emailOptions.caseInfo,
    };

    return {
      success: true,
      offersData: offersResult.data,
      workupsData: attachBatchWorkups ? (workupsResult?.data || []) : [],
      emailOptions,
    };
  } catch (error) {
    logger.error('❌ [EMAIL FACTORY] Error preparing offers/workups email:', error);
    return {
      success: false,
      error: error.message || 'Failed to prepare offers/workups email',
    };
  }
};